import React from 'react';
import { OrderCombination, OrderProduct } from '../../interfaces';
import '../itemActions.css';

interface EditOrderItemPopupProps {
    onClose: () => void;
    onEditNote: () => void;
    onEditAddon: () => void;
    onEditVariant: () => void;
    onEditDiscount: () => void;
    onEditProductsForFlexibleCombination: () => void;
    orderProduct: OrderProduct | null;
    orderCombination: OrderCombination | null;
}

const Chev: React.FC = () => (
    <span className="tia-chev"><svg width="8" height="13" viewBox="0 0 8 13" fill="none"><path d="M1.5 1.5L6.5 6.5L1.5 11.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
);

const NoteIcon = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5Z" /></svg>;
const VariantIcon = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M10.5 4h.4c3 0 4.6 0 5.1.5.5.5.7 1.2.6 1.9-.2.8-1.4 1.6-3.9 3.4l-4 2.9c-2.5 1.7-3.7 2.6-3.9 3.4a1.8 1.8 0 0 0 .6 1.8c.6.6 2.1.6 5.1.6H11M7 4a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm14 14a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" /></svg>;
const AddonIcon = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M7 16V4a2.6 2.6 0 0 0-.85-2.08A2.6 2.6 0 0 0 4 1a2.6 2.6 0 0 0-2.15.92A2.6 2.6 0 0 0 1 4v12c0 .93 0 1.4.15 1.77.2.49.59.88 1.08 1.08C2.6 19 3.07 19 4 19s1.4 0 1.77-.15c.49-.2.88-.59 1.08-1.08C7 17.4 7 16.93 7 16Zm10-2V6a2.6 2.6 0 0 0-.85-2.08A2.6 2.6 0 0 0 14 3a2.6 2.6 0 0 0-2.15.92A2.6 2.6 0 0 0 11 6v8c0 .93 0 1.4.15 1.77.2.49.59.88 1.08 1.08C12.6 17 13.07 17 14 17s1.4 0 1.77-.15c.49-.2.88-.59 1.08-1.08C17 15.4 17 14.93 17 14Z" /></svg>;
const DiscIcon = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 9h.01M15 15h.01M16 8 8 16M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>;
const ComboIcon = <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h18M3 12h18M3 17h18" /></svg>;

const EditOrderItemPopup: React.FC<EditOrderItemPopupProps> = ({ onClose, onEditNote, onEditAddon, onEditVariant, onEditDiscount, orderProduct, orderCombination, onEditProductsForFlexibleCombination }) => {
    const hasNote = (orderProduct?.note ?? orderCombination?.note ?? '') !== '';
    const hasVariants = !!(orderProduct?.product?.variants?.length && orderProduct?.variants?.length);
    const hasAddons = !!(orderProduct?.product?.addons?.length && orderProduct?.addons?.length);
    const hasDiscount = (orderProduct?.discount ?? orderCombination?.discount ?? 0) !== 0;
    const isFlexCombo = orderCombination != null && orderCombination.type === 1;
    const name = orderProduct?.name ?? orderCombination?.combinationName ?? 'this item';

    const Row: React.FC<{ icon: React.ReactNode; title: string; sub: string; onClick: () => void }> = ({ icon, title, sub, onClick }) => (
        <button className="tia-erow" onClick={onClick}>
            <span className="tia-erow__ic">{icon}</span>
            <span className="tia-erow__t"><b>{title}</b><small>{sub}</small></span>
            <Chev />
        </button>
    );

    return (
        <div className="tia-card">
            <div className="tia-mhead">
                <div>
                    <div className="tia-mtitle">Edit item</div>
                    <div className="tia-msub">{name}</div>
                </div>
                <button className="tia-x" onClick={onClose} aria-label="Close">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
            </div>
            <div className="tia-mbody tia-mbody--list">
                {hasNote && <Row icon={NoteIcon} title="Edit note" sub={orderProduct?.note || orderCombination?.note || 'Kitchen instructions'} onClick={onEditNote} />}
                {hasVariants && <Row icon={VariantIcon} title="Edit variants" sub="Change the selected option" onClick={onEditVariant} />}
                {hasAddons && <Row icon={AddonIcon} title="Edit add-ons" sub="Change extras and quantities" onClick={onEditAddon} />}
                {hasDiscount && <Row icon={DiscIcon} title="Edit discount" sub="Adjust or remove the discount" onClick={onEditDiscount} />}
                {isFlexCombo && <Row icon={ComboIcon} title="Edit products" sub="Change items in this combo" onClick={onEditProductsForFlexibleCombination} />}
            </div>
            <div className="tia-mfoot">
                <span />
                <button className="tia-btn tia-btn--ghost" onClick={onClose}>Cancel</button>
            </div>
        </div>
    );
};

export default EditOrderItemPopup;
